<template>
    <div class="cp-store-locator__search">
        <p class="h1">{{ labels.title }}</p>

        <input type="text" :placeholder="labels.placeholder_search" class="cp-store-locator__search__text" ref="autocomplete" />

        <div class="cp-store-locator__search__number">
            <input type="number" value="50" min="1" v-model="selectedRadius" @change="filterRadius" />
            <label>Km</label>
        </div>

        <div v-if="!config.hide_countries">
            <label>{{ labels.country }}</label>

            <vue-select :options="computedCountries" :placeholder="labels.placeholder_country" @input="filterCountry"></vue-select>
        </div>

        <div class="cp-store-locator__search__categories" v-if="!config.hide_categories">
            <label class="cp-store-locator__search__categories__el" v-for="( name, slug ) in categories">
                <input type="checkbox" :value="slug" :checked="selectedCategories.includes( slug )" @change="filterCategories( $event )">

                <span>{{ name }}</span>
            </label>
        </div>
    </div>
</template>

<script>
import VueSelect from 'vue-select'
import 'vue-select/dist/vue-select.css';

export default {
    name: "cp-store-locator-search",
    components: {
        VueSelect,
    },
    computed: {
        computedCountries() {
            return Object.keys( this.countries ).map( ( key ) => {
                return {
                    label: this.countries[ key ],
                    key: key,
                }
            } );
        },
    },
    props: {
        config: {
            type: Object,
            required: true,
        },

        labels: {
            type: Object,
            required: true,
        },

        countries: {
            type: Object,
            required: true,
        },

        categories: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            search: '',
            selectedCountry: '',
            selectedCategories: Object.keys( this.categories ),
            selectedRadius: 50,
            selectedCoordinates: {
                lat: '',
                lng: '',
            },
            autocompleteInput: '',
        };
    },
    methods: {
        filterRadius(e)
        {
            this.$actions.applyFilters();
        },

        filterCountry(country)
        {
            this.selectedCountry = country ? country.label : '';

            this.$actions.applyFilters();
        },

        filterCategories($event) {
            if ( $event.target.checked && !this.selectedCategories.includes( $event.target.value ) ) {
                this.selectedCategories.push( $event.target.value );
            } else {
                const index = this.selectedCategories.indexOf( $event.target.value );

                if ( index > -1 ) {
                    this.selectedCategories.splice( index, 1 );
                }
            }

            this.$actions.applyFilters();
        },

        createAutocomplete() {
            if ( this.autocompleteInput ) {
                window.google.maps.event.clearInstanceListeners( this.autocompleteInput );
            }

            const config = this.config.autocomplete ?? {};

            this.autocompleteInput = new window.google.maps.places.Autocomplete( this.$refs['autocomplete'], config );

            this.autocompleteInput.addListener('place_changed', () => {
                const place = this.autocompleteInput.getPlace();

                if ( place.name ) {
                    if ( place.address_components ) {
                        const lat = place.geometry.location.lat();
                        const lng = place.geometry.location.lng();

                        this.selectedCoordinates.lat = lat;
                        this.selectedCoordinates.lng = lng;

                        this.$events.emit('center-map', lat, lng, 10);

                        this.$actions.applyFilters();
                    } else {
                        const autocompleteService = new window.google.maps.places.AutocompleteService();

                        autocompleteService.getPlacePredictions( {
                            ...config,
                            input:  place.name,
                            offset: place.name.length,
                        }, ( list, status ) => {
                            if ( list && list.length ) {
                                this.query = list[0].description;

                                const placesServices = new window.google.maps.places.PlacesService( this.$refs['autocomplete'] );

                                placesServices.getDetails(
                                    {reference: list[0].reference},
                                    ( serviceResult, serviceStatus ) => {
                                        if ( serviceStatus === window.google.maps.GeocoderStatus.OK ) {
                                            this.autocompleteInput.set('place', serviceResult);
                                        }
                                    }
                                );
                            }
                        } );

                        this.$actions.applyFilters();
                    }
                } else {
                    // @TODO - reset map position and zoom
                    this.$events.emit('clear-circle');
                    this.$events.emit('clear-current-marker');
                    this.$events.emit('set-displayed-markers', null);

                    this.selectedCoordinates.lat = '';
                    this.selectedCoordinates.lng = '';
                }
            });
        },
    },
    mounted() {
        this.$events.on('api-loaded', () => {
            this.$actions.registerFilter( stores => stores.filter( store => {
                if ( this.selectedCoordinates.lat === '' || this.selectedCoordinates.lng === '' ) {
                    return store;
                }

                const R = 6371e3; // metres
                const lat_radius1 = store.lat * Math.PI / 180; // φ, λ in radians
                const lat_radius2 = this.selectedCoordinates.lat * Math.PI / 180;
                const lat_difference = ( this.selectedCoordinates.lat - store.lat ) * Math.PI / 180;
                const lng_difference = ( this.selectedCoordinates.lng - store.lng ) * Math.PI / 180;

                const a = Math.sin( lat_difference / 2 ) * Math.sin( lat_difference / 2 ) +
                          Math.cos( lat_radius1 ) * Math.cos( lat_radius2 ) *
                          Math.sin( lng_difference / 2 ) * Math.sin( lng_difference / 2 );

                const c = 2 * Math.atan2( Math.sqrt( a ), Math.sqrt( 1 - a ) );

                const d = R * c; // in metres

                return this.selectedRadius >= Math.round( d / 1000 );
            } ) );

            if ( !this.config.hide_countries ) {
                this.$actions.registerFilter( stores => stores.filter( store =>
                    this.selectedCountry === '' || store.country === this.selectedCountry
                ) );
            }

            if ( !this.config.hide_categories ) {
                this.$actions.registerFilter( stores => stores.filter( store =>
                    this.selectedCategories.filter( category => store.categories !== undefined && Object.keys( store.categories ).includes( category ) ).length > 0
                ) );
            }

            this.createAutocomplete();
        } );
    }
}
</script>
